/** @jsxImportSource @emotion/react */

/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import { useOutsideClick } from "@imwebme/clay-hooks";
import type { PropsWithChildren } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { BaseProps } from "../types";
import { Portal } from "../utilities/Portal";
import { useSelectOff, useSelectState } from "./Select.provider";

export type SelectPortalProps = PropsWithChildren<{
  offsetY?: string;
  sx?: BaseProps["sx"];
}>;

export const SelectPortal = ({ children, offsetY = "0px", sx }: SelectPortalProps) => {
  const { portalRef } = useSelectState();
  const { isOpen, closeOnSelect } = useSelectState();
  const ref = useRef<HTMLDivElement>(null);
  const setOff = useSelectOff();
  const [portalWidth, setPortalWidth] = useState(0);
  const [localOpenState, setLocalOpenState] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setLocalOpenState(isOpen);
    }, 0);
  }, [isOpen]);

  useOutsideClick({
    ref: ref.current,
    callback: () => {
      setTimeout(() => {
        setOff();
      }, 0);
    },
    enabled: isOpen,
  });

  useLayoutEffect(() => {
    const resizeCallback = () => {
      if (portalRef) setPortalWidth(portalRef.clientWidth);
    };

    if (isOpen) window.addEventListener("resize", resizeCallback);

    return () => {
      if (isOpen) window.removeEventListener("resize", resizeCallback);
    };
  }, [isOpen, portalRef]);

  return (
    <Portal.Absolute
      to={isOpen ? portalRef : null}
      position="bottom"
      align="start"
      offsetY={`calc(8px + ${offsetY})`}
      autoMinWidth
    >
      <div
        ref={ref}
        onClick={() => (closeOnSelect ? setOff() : undefined)}
        css={{
          ...sx,
          transition: "margin 200ms ease-out",
          opacity: localOpenState ? 1 : 0,
          width: portalWidth || portalRef?.clientWidth,
          marginTop: localOpenState ? 0 : "-8px",
        }}
      >
        {children}
      </div>
    </Portal.Absolute>
  );
};
